import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

// Real project images - verified mapping
const images = {
  blueTileBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/fpgzjpp3_Before%202.jpeg",
  blueTileAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/iqdzyqzo_After%202.jpeg",
  // Stone/masonry pair - SWAPPED based on visual inspection
  stoneBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/926zc2jx_After%201.jpeg",
  stoneAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/v9fe3x5w_Before%201.jpeg",
  poolPrepBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/uz4ov2h2_Before%203.jpeg",
  poolPrepAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/lncc0qq7_After%203.jpeg",
  industrialBlasting: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/hi1c3ccr_478AF081-9B7D-41F1-9192-EA57D9B9898A.jpeg",
};

const services = [
  {
    id: "pool-tile",
    title: "Pool Tile Calcium Removal",
    subtitle: "Restore clarity without damage",
    description: "Years of hard water deposits and calcium buildup don't stand a chance. We restore pool tiles to their original condition using precision media that cleans without damaging tile surfaces or grout lines. Austin's hard water makes this our most requested service.",
    image: images.blueTileAfter,
    beforeImage: images.blueTileBefore,
    afterImage: images.blueTileAfter,
    hasBeforeAfter: true,
    features: [
      "Remove stubborn calcium deposits",
      "Preserve tile integrity",
      "No damage to grout",
      "Works on all tile types",
      "Same-day service available",
    ],
  },
  {
    id: "stone-restoration",
    title: "Stone & Masonry Restoration",
    subtitle: "Natural beauty, restored",
    description: "From flagstone patios to limestone facades, we bring natural stone back to life. Our media blasting approach cleans deeply without the surface damage caused by pressure washing or harsh chemicals.",
    image: images.stoneAfter,
    beforeImage: images.stoneBefore,
    afterImage: images.stoneAfter,
    hasBeforeAfter: true,
    features: [
      "Flagstone & pavers",
      "Limestone & sandstone",
      "Brick & masonry walls",
      "Concrete surfaces",
      "Exterior facades",
    ],
  },
  {
    id: "pool-prep",
    title: "Pool Prep & Surface Blasting",
    subtitle: "Prepare for perfection",
    description: "Proper surface preparation is critical for replastering, refinishing, or applying new coatings. We remove old material and create the ideal surface profile for maximum adhesion and longevity.",
    image: images.poolPrepAfter,
    beforeImage: images.poolPrepBefore,
    afterImage: images.poolPrepAfter,
    hasBeforeAfter: true,
    features: [
      "Replaster preparation",
      "Coating removal",
      "Surface profiling",
      "Complete debris removal",
      "Ready for refinishing",
    ],
  },
  {
    id: "commercial",
    title: "Specialty & Commercial",
    subtitle: "Scale without compromise",
    description: "Large-scale projects demand professional execution. Fleet vehicles, architectural surfaces, industrial equipment—we bring the same precision to commercial contracts that homeowners expect.",
    image: images.industrialBlasting,
    hasBeforeAfter: false,
    features: [
      "Fleet & trailer prep",
      "Architectural facades",
      "Industrial equipment",
      "Custom stencil etching",
      "Volume project capacity",
    ],
  },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-surface">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } },
            }}
            className="max-w-4xl"
          >
            <motion.span variants={fadeUp} transition={{ duration: 0.6 }} className="label-accent block mb-6">
              Services
            </motion.span>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight mb-8"
            >
              What We
              <br />
              <span className="text-text-secondary">Restore</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-text-secondary text-lg md:text-xl max-w-2xl leading-relaxed"
            >
              From pool tile calcium removal to commercial surface prep—precision media blasting that delivers visible transformation without damage.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="space-y-32">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                data-testid={`service-${service.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <span className="label-accent block mb-4">{service.subtitle}</span>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-text-primary mb-6">
                    {service.title}
                  </h2>
                  <p className="text-text-secondary text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-text-secondary">
                        <Check className="w-4 h-4 text-accent flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/quote"
                    data-testid={`service-${service.id}-quote`}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Get Quote
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  {service.hasBeforeAfter ? (
                    <BeforeAfterSlider
                      beforeImage={service.beforeImage}
                      afterImage={service.afterImage}
                    />
                  ) : service.image ? (
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 border border-white/5" />
                    </div>
                  ) : (
                    <div className="relative aspect-[4/3] overflow-hidden bg-surface-highlight border border-white/5 flex items-center justify-center">
                      <div className="text-center p-8">
                        <span className="label-accent block mb-2">Commercial Projects</span>
                        <p className="text-text-secondary text-sm">Contact us to discuss your project</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-surface">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-accent block mb-6">Not Sure Which Service?</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary mb-6">
              Let's discuss your project
            </h2>
            <p className="text-text-secondary text-lg mb-10">
              Describe your surface and goals. We'll recommend the right approach and provide a free estimate.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/quote"
                data-testid="services-cta-quote"
                className="btn-primary inline-flex items-center gap-2"
              >
                Get Free Quote
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="services-cta-contact"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
